/**
 * Ahrefs Keyword Intelligence Service
 *
 * Provides access to Ahrefs-powered keyword research features:
 * - Keyword research with trends
 * - Competitor keyword analysis
 * - Content gap discovery
 * - Top pages analysis
 */

import { apiClient } from './api';

// =============================================================================
// Types
// =============================================================================

export interface KeywordMetrics {
  keyword: string;
  volume: number;
  difficulty: number;
  cpc?: number;
  trend?: 'rising' | 'stable' | 'declining';
  source?: 'seed' | 'competitor' | 'matching';
}

export interface KeywordResearchRequest {
  seed_keyword: string;
  country?: string;
  limit?: number;
  min_volume?: number;
  max_difficulty?: number;
}

export interface KeywordResearchResponse {
  seed_keyword: string;
  total_keywords: number;
  keywords: KeywordMetrics[];
}

export interface TrendDataPoint {
  date: string;
  volume: number;
}

export interface CountryVolume {
  country: string;
  volume: number;
  percentage: number;
}

export interface KeywordContextRequest {
  keyword: string;
  country?: string;
}

export interface KeywordContextResponse {
  keyword: string;
  volume: number;
  difficulty: number;
  cpc?: number;
  trend?: 'rising' | 'stable' | 'declining';
  trend_data: TrendDataPoint[];
  global_volume: number;
  top_countries: CountryVolume[];
}

export interface CompetitorKeywordsRequest {
  target_url: string;
  country?: string;
  limit?: number;
  min_volume?: number;
  max_difficulty?: number;
}

export interface CompetitorKeyword {
  keyword: string;
  volume: number;
  difficulty: number;
  position: number;
  traffic: number;
  url?: string;
}

export interface CompetitorKeywordsResponse {
  target: string;
  total_keywords: number;
  total_traffic: number;
  avg_position: number;
  keywords: CompetitorKeyword[];
}

export interface ContentGapRequest {
  your_domain: string;
  competitor_domains: string[];
  country?: string;
  limit?: number;
  min_volume?: number;
}

export interface ContentGapKeyword {
  keyword: string;
  volume: number;
  difficulty: number;
  opportunity_score: number;
  competitor_count: number;
  competitors: string[];
}

export interface ContentGapResponse {
  your_domain: string;
  competitor_domains: string[];
  total_opportunities: number;
  keywords: ContentGapKeyword[];
}

export interface TopPageRequest {
  target: string;
  country?: string;
  limit?: number;
}

export interface TopPage {
  url: string;
  traffic: number;
  keywords: number;
  top_keyword?: string;
}

export interface TopPagesResponse {
  target: string;
  total_pages: number;
  total_traffic: number;
  pages: TopPage[];
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Research keywords based on a seed keyword
 */
export async function researchKeywords(
  request: KeywordResearchRequest
): Promise<KeywordResearchResponse> {
  const response = await apiClient.post<KeywordResearchResponse>(
    '/ahrefs/keyword-research',
    request
  );
  return response.data;
}

/**
 * Get comprehensive keyword context including trends
 */
export async function getKeywordContext(
  request: KeywordContextRequest
): Promise<KeywordContextResponse> {
  const response = await apiClient.post<KeywordContextResponse>(
    '/ahrefs/keyword-context',
    request
  );
  return response.data;
}

/**
 * Analyze competitor keywords
 */
export async function getCompetitorKeywords(
  request: CompetitorKeywordsRequest
): Promise<CompetitorKeywordsResponse> {
  const response = await apiClient.post<CompetitorKeywordsResponse>(
    '/ahrefs/competitor-keywords',
    request
  );
  return response.data;
}

/**
 * Find content gap opportunities
 */
export async function getContentGap(
  request: ContentGapRequest
): Promise<ContentGapResponse> {
  const response = await apiClient.post<ContentGapResponse>(
    '/ahrefs/content-gap',
    request
  );
  return response.data;
}

/**
 * Get top performing pages for a domain
 */
export async function getTopPages(
  request: TopPageRequest
): Promise<TopPagesResponse> {
  const response = await apiClient.post<TopPagesResponse>(
    '/ahrefs/top-pages',
    request
  );
  return response.data;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get trend display info
 */
export function getTrendInfo(trend?: string): { label: string; color: string; icon: string } {
  switch (trend) {
    case 'rising':
      return { label: 'Rising', color: '#52c41a', icon: '↑' };
    case 'declining':
      return { label: 'Declining', color: '#f5222d', icon: '↓' };
    case 'stable':
    default:
      return { label: 'Stable', color: '#8c8c8c', icon: '→' };
  }
}

/**
 * Get opportunity score color
 */
export function getOpportunityColor(score: number): string {
  if (score >= 80) return '#52c41a'; // Green - excellent
  if (score >= 60) return '#73d13d'; // Light green - good
  if (score >= 40) return '#faad14'; // Yellow - moderate
  if (score >= 20) return '#fa8c16'; // Orange - low
  return '#f5222d'; // Red - poor
}

/**
 * Format large numbers with K/M suffix
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

export const ahrefsService = {
  researchKeywords,
  getKeywordContext,
  getCompetitorKeywords,
  getContentGap,
  getTopPages,
  getTrendInfo,
  getOpportunityColor,
  formatNumber,
};

export default ahrefsService;
